// Shared form-validation vocabulary: the zod half of the convention introduced with
// public/Reserver.jsx's validateBookingForm() and generalized here for the ~70 forms
// that follow.
//
// ── THE CONVENTION: a zod `message` is an i18n KEY, never prose ────────────────────
// Every message below is a dictionary key (`"validation.required"`), resolved by the
// CALLER through its own `t` binding. Two reasons, and both are load-bearing:
//   * a schema is a module-scope value — it cannot call a hook, so it has no access to
//     the active language; only the component rendering the error does;
//   * the same schema is shared by three locales. Prose baked into the schema would be
//     one language's prose leaking into the other two (cf. the language law in
//     CLAUDE.md: user-facing strings always go through the locale layer).
// Consequence: a message that is NOT a key renders verbatim to the user, because
// lookup() falls back to the key itself. src/__tests__/validationKeys.static.test.js
// scans the whole tree for message literals and fails on any key that no dictionary
// defines; lib/__tests__/validation.test.js pins the vocabulary this file emits.
//
// This module is PURE: zod only. No hook, no axios, no React — the same discipline as
// lib/apiErrors.js and lib/checkoutErrors.js, which is what makes it directly unit
// testable (an `@/lib/api` import would make it unimportable under Jest).
//
// ── THE CANONICAL CALLER RECIPE ────────────────────────────────────────────────────
// Paste this shape into a form; do not invent a variant.
//
//   const tv = useT("common");
//   const { fieldErrors, validate, clearFieldError } = useFieldErrors();
//   const updateField = (k, v) => { setForm(f => ({...f, [k]: v})); clearFieldError(k); };
//   const save = async () => {
//     if (validate(schema, form)) { toast.error(tv("validation.fixHighlighted")); return; }
//     setBusy(true);
//     try { /* api call */ }
//     catch (e) { toast.error(extractApiError(e, tv("errors.saveFailed"), tv)); }
//     finally { setBusy(false); }
//   };
//   // Save button: disabled={busy} ONLY — never disabled for content validity (house rule).
//
// The house rule at the end is not cosmetic: a button disabled because a field is empty
// states no reason the user can act on. Judging content ON CLICK is what lets the form
// point at the offending field instead.
import { z } from "zod";

/**
 * Runs a schema and flattens zod's issue list into one message per field.
 *
 * FIRST ISSUE PER FIELD WINS. Zod reports every failed check on a value — `""` fails
 * both `min(1)` and `email()` — and the first one is the most specific statement about
 * what the user actually did (they left it blank; they did not mistype an address).
 *
 * @param {import("zod").ZodType} schema
 * @param {object} form  the raw form state, shape `{[field: string]: unknown}`
 * @returns {Record<string, string>|null} `{field: i18nKey}`, or `null` when valid.
 *   An issue with no path (a `superRefine` on the object itself that names no field) is
 *   keyed `_form`, so a submit blocked by a form-level rule can still say why instead of
 *   returning a silently empty error map.
 */
export const validateWith = (schema, form) => {
  const result = schema.safeParse(form);
  if (result.success) return null;
  const fieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path.length ? issue.path[0] : "_form";
    if (!fieldErrors[field]) fieldErrors[field] = issue.message;
  }
  return fieldErrors;
};

/**
 * A required email address. Check order matters: `min(1)` before `email()` so a blank
 * field reads "required", and `max(254)` (the RFC 5321 ceiling, which the backend also
 * enforces) before `email()` so an absurd value is one message, not two.
 */
export const emailField = z.string().trim()
  .min(1, "validation.emailRequired")
  .max(254, "validation.emailInvalid")
  .email("validation.emailInvalid");

/**
 * An email that may be left empty — an optional contact field, not an identity.
 * `errorMap` is what keeps the union's OWN issue an i18n key: a failed `z.union` reports
 * a single `invalid_union` issue whose default message is the English literal
 * "Invalid input", and that string would render verbatim to a French user.
 */
export const optionalEmailField = z.union([z.literal(""), emailField], {
  errorMap: () => ({ message: "validation.emailInvalid" }),
});

/**
 * The predicate form, for the places that need a boolean rather than an error map
 * (enabling a "send code" action, gating an autocomplete). Replaces the ad-hoc
 * `EMAIL_RE` regexes: same judgement everywhere, defined once.
 */
export const isValidEmail = (value) => emailField.safeParse(value).success;

/**
 * Blank-or-valid, trim-first: is `value` empty after trimming, or a syntactically valid
 * email once trimmed? The predicate behind an OPTIONAL contact field whose caller ALSO
 * normalises the value on the wire (trims, then sends `null` for anything that trims to
 * "" — e.g. admin/settings/NotificationSettingsCard.jsx's recipient override).
 *
 * This is deliberately NOT the same rule as `optionalEmailField`: that field's blank
 * branch is an exact `z.literal("")`, so a whitespace-only value is not "" and falls
 * through to `emailField`, which then rejects it as too short. That is correct for a
 * field the user is actively typing an address into, but wrong here — the validity check
 * and the write must agree on what "blank" means, or a value that saves cleanly (trimmed
 * to `null`) can still get flagged red by validation on the way there.
 *
 * Exported as a PLAIN PREDICATE, not a zod field: the caller needs its own i18n key for
 * the failure (the zod-message-is-a-key convention above), so it builds its own
 * `superRefine`/`ctx.addIssue` around this — which keeps that key a static string literal
 * at the call site, the shape validationKeys.static.test.js's harvest can actually see.
 */
export const isBlankOrValidEmail = (value) => {
  const v = typeof value === "string" ? value.trim() : "";
  return v === "" || isValidEmail(v);
};

/**
 * A required free-text field. `max` defaults to 200 — the length of a title or a name,
 * the common case; pass the backend's own ceiling whenever it is known (a schema that
 * is laxer than the API just moves the rejection to a 422).
 */
export const requiredField = (max = 200) => z.string().trim()
  .min(1, "validation.required")
  .max(max, "validation.tooLong");

/**
 * A required field whose missing value is specifically the FRENCH one, in a
 * language-aware pair (`title_fr` / `title_en`). The distinct key exists because
 * "Required" on a field whose FR tab is not even visible tells the practitioner nothing
 * — the message has to name the language.
 */
export const frRequiredField = (max = 200) => z.string().trim()
  .min(1, "validation.frRequired")
  .max(max, "validation.tooLong");

/**
 * "At least one language" rule for a localized pair: both blank is refused, either one
 * alone is accepted. The issue is attached to the FR field because that is the tab the
 * dashboard opens on for a FR-first product — the error must land where the user is
 * looking, which is also what lets LanguageAwareField switch to it.
 *
 * @param {import("zod").ZodObject} schema  the object schema to wrap
 * @param {string} frField  key of the French value in the form
 * @param {string} enField  key of the English value in the form
 */
export const withAnyLangRule = (schema, frField, enField) =>
  schema.superRefine((val, ctx) => {
    const blank = (v) => !(typeof v === "string" && v.trim().length > 0);
    if (blank(val?.[frField]) && blank(val?.[enField])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [frField],
        message: "validation.anyLangRequired",
      });
    }
  });

/**
 * A bare FQDN — no scheme, no path. Mirrors backend saas_models.py's own `DOMAIN_RE`
 * exactly; that backend copy stays separate on purpose (self-flagged there as a
 * circular-import constraint), so the two are hand-synced, not shared code.
 *
 * On the frontend, this used to be TWO byte-identical copies — admin/SiteBuilderWizard.jsx's
 * address step and admin/CustomDomainAdmin.jsx's dedicated page — with no shared home, so a
 * rule change to one could silently diverge from the other. One export now; both import it.
 * Each caller still builds its OWN schema around it (one treats a blank value as "no custom
 * domain, that's fine"; the other requires one, and pre-strips a pasted scheme/path) — that
 * requiredness and pre-processing is genuinely per-form, only the format rule is shared.
 */
export const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z0-9-]{1,63})+$/;

/**
 * The predicate form, for a caller that wants a boolean rather than building its own
 * regex test — mirrors isValidEmail's role for emailField. Lowercases internally: DOMAIN_RE
 * is deliberately case-sensitive (lowercase-only charset) so the two admin call sites,
 * which lowercase by hand before testing, aren't relying on that unstated by accident;
 * a fresh caller going through this predicate does not have to know that.
 */
export const isValidDomain = (value) => typeof value === "string" && DOMAIN_RE.test(value.toLowerCase());
